/**
 * Trusted, per-tab navigation entries. The browser index distinguishes a
 * push from replace and makes it impossible to back into an unobserved entry.
 */
export class AppNavigationHistory {
  private entries = new Map<number, string>();
  private current: number | null = null;

  observe(key: string, index: number): void {
    if (!Number.isSafeInteger(index) || index < 0) return;
    // A new branch invalidates any observed forward entries. Back/forward
    // to an existing entry, however, must retain them.
    if (this.entries.get(index) !== key) {
      for (const known of this.entries.keys()) {
        if (known > index) this.entries.delete(known);
      }
    }
    this.entries.set(index, key);
    this.current = index;
  }

  canGoBack(key: string, index: number): boolean {
    return (
      this.current === index &&
      this.entries.get(index) === key &&
      this.entries.has(index - 1)
    );
  }
}

export const appNavigationHistory = new AppNavigationHistory();

export interface AppHistoryEntry {
  key: string;
  index: number;
}

/** TanStack History sets these fields on entries it owns. */
export function currentHistoryEntry(): AppHistoryEntry | null {
  const state = window.history.state as {
    __TSR_key?: unknown;
    __TSR_index?: unknown;
  } | null;
  return typeof state?.__TSR_key === "string" &&
    typeof state.__TSR_index === "number" &&
    Number.isSafeInteger(state.__TSR_index)
    ? { key: state.__TSR_key, index: state.__TSR_index }
    : null;
}
